import { Injectable } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { AlertFacade } from '@curbnturf/alert/src/lib/+state/alert.facade';
import {
  IAccessRoute,
  IActivity,
  IActivityType,
  IAmenity,
  ICancellationPolicyOption,
  ICheckInProcessType,
  IKvPair,
  ILandOwner,
  ILandType,
  IListing,
  IListingExtra,
  INoise,
  IReservationProcessType,
  ISite,
  IWaypoint,
  Listing,
} from '@curbnturf/entities';
import { ListingFacade } from '@curbnturf/listing/src/lib/+state/listing.facade';
import { DateTime } from 'luxon';
import { getFormValidationErrors } from '../helpers/errors';
import {
  addressFormBuilder,
  coordinatesFormBuilder,
  extraFormBuilder,
  formatAddressFormValue,
  formatCoordinatesFormValue,
  formatPhotosFormValue,
  formatPolicyFormValue,
  formatRoadConditionsFormValue,
  formatSignalReceptionFormValue,
  formatSiteExtraFormValue,
  formatWaypointFormValue,
  photoFormBuilder,
  policyFormBuilder,
  roadConditionsFormBuilder,
  signalReceptionFormBuilder,
} from './builder.helpers';
import {
  IAccessRouteForm,
  IActivityForm,
  IAddressForm,
  IAmenityForm,
  ICoordinatesForm,
  ILandOwnerForm,
  IListingExtraForm,
  IListingForm,
  INoiseForm,
  IPhotoForm,
  IPolicyForm,
  IRoadConditionsForm,
  ISignalReceptionForm,
  IWaypointForm,
} from './interfaces';

@Injectable({
  providedIn: 'root',
})
export class ListingForm {
  accessRouteGroup: () => IAccessRouteForm;
  activityGroup: (activityType?: IKvPair) => IActivityForm;
  landOwnerGroup: () => ILandOwnerForm;
  noiseGroup: () => INoiseForm;
  listingForm: FormGroup<IListingForm>;

  errors: string[] = [];
  lastSaved: DateTime;

  get blackoutDays() {
    if (this.listingForm) {
      return this.listingForm.get('blackoutDays') as FormArray<FormControl<string>>;
    }

    return;
  }

  get blackoutMonths() {
    if (this.listingForm) {
      return this.listingForm.controls.blackoutMonths;
    }

    return;
  }

  get extras() {
    if (this.listingForm) {
      return this.listingForm.controls.extras;
    }

    return;
  }

  get landTypes() {
    if (this.listingForm) {
      return this.listingForm.get('landTypes') as FormArray<FormControl<string>>;
    }

    return;
  }

  get photos() {
    if (this.listingForm) {
      return this.listingForm.get('photos') as FormArray<FormGroup<IPhotoForm>>;
    }

    return;
  }

  get policies() {
    if (this.listingForm) {
      return this.listingForm.get('policies') as FormArray<FormGroup<IPolicyForm>>;
    }

    return;
  }

  get signalReception() {
    if (this.listingForm) {
      return this.listingForm.get('signalReception') as FormArray<FormGroup<ISignalReceptionForm>>;
    }

    return;
  }

  static formatListingFormValue(form: FormGroup<IListingForm>): IListing {
    const value = form.getRawValue();

    return {
      id: value.id || undefined,
      available: value.available === null || value.available === undefined ? undefined : value.available,
      name: value.name || undefined,
      landTypes: (value.landTypes?.filter((type) => type) as ILandType['type'][]) || [],
      coordinates: formatCoordinatesFormValue(form.get('coordinates') as FormGroup<ICoordinatesForm>),
      reservationProcess: value.reservationProcess || 'standard',
      checkInProcess: value.checkInProcess || undefined,
      checkIn: value.checkIn || undefined,
      checkOut: value.checkOut || undefined,
      description: value.description || undefined,
      weekdayDiscount: value.weekdayDiscount || undefined,
      nextReservationDiscount: value.nextReservationDiscount === null ? undefined : value.nextReservationDiscount,
      blackoutMonths: (value.blackoutMonths?.filter((month) => typeof month === 'number') as number[]) || [],
      blackoutDays: (value.blackoutDays?.filter((day) => day) as string[]) || [],
      photos: formatPhotosFormValue(form.get('photos') as FormArray<FormGroup<IPhotoForm>>),
      size: value.size || undefined,
      extras: (form.get('extras') as FormArray<FormGroup<IListingExtraForm>>)?.controls?.map((extraForm) =>
        formatSiteExtraFormValue(extraForm),
      ),
      signalReception: (form.get('signalReception') as FormArray<FormGroup<ISignalReceptionForm>>)?.controls?.map(
        (signalReceptionForm) => formatSignalReceptionFormValue(signalReceptionForm),
      ),
      maxRVAge: value.maxRVAge || undefined,
      minLengthOfStayInDays: value.minLengthOfStayInDays || undefined,
      maxLengthOfStayInDays: value.maxLengthOfStayInDays || undefined,
      bookingWindow: value.bookingWindow || undefined,
      reserveDaysInAdvance: value.reserveDaysInAdvance === null ? undefined : value.reserveDaysInAdvance,
      cancellationPolicy: value.cancellationPolicy || undefined,
      policies: (form.get('policies') as FormArray<FormGroup<IPolicyForm>>)?.controls?.map((policyForm) =>
        formatPolicyFormValue(policyForm),
      ),
      landOwner: ListingForm.formatLandOwnerFormValue(form.get('landOwner') as FormGroup<ILandOwnerForm>),
      address: formatAddressFormValue(form.get('address') as FormGroup<IAddressForm>),
      potentialHazards:
        value.potentialHazards === null || value.potentialHazards === undefined ? undefined : value.potentialHazards,
      potentialHazardDescription: value.potentialHazardDescription || undefined,
    };
  }

  static formatNoiseFormValue(form?: FormGroup<INoiseForm>): INoise {
    if (!form) {
      return {};
    }

    const value = form.value;

    return {
      level: value.level === null ? undefined : value.level,
      description: value.description || undefined,
    };
  }

  static formatAccessRouteFormValue(form?: FormGroup<IAccessRouteForm>): IAccessRoute {
    if (!form) {
      return {
        roadCondition: {
          paved: false,
          pavedDescription: '',
          smoothGravel: false,
          smoothGravelDescription: '',
          poorGravel: false,
          poorGravelDescription: '',
          highClearance: false,
          highClearanceDescription: '',
          offRoad: false,
          offRoadDescription: '',
        },
      };
    }

    const value = form.value;

    return {
      id: value.id || undefined,
      name: value.name || undefined,
      description: value.description || undefined,
      directions: value.directions || undefined,
      startingPoint: formatCoordinatesFormValue(form.get('startingPoint') as FormGroup<ICoordinatesForm>),
      maxHeight: value.maxHeight || undefined,
      maxLength: value.maxLength || undefined,
      maxWidth: value.maxWidth || undefined,
      minClearance: value.minClearance || undefined,
      intermediateWaypoints: (form.get('intermediateWaypoints') as FormArray<FormGroup<IWaypointForm>>)?.controls
        ?.map((waypointForm) => formatWaypointFormValue(waypointForm))
        .filter((waypoint) => waypoint) as IWaypoint[],
      roadCondition: formatRoadConditionsFormValue(form.get('roadCondition') as FormGroup<IRoadConditionsForm>),
    };
  }

  static formatAmenityFormValue(form?: FormGroup<IAmenityForm>): IAmenity {
    if (!form) {
      return {};
    }

    const value = form.value;

    return {
      id: value.id || undefined,
      type: value.type || undefined,
      custom: value.custom || '',
      options: value.options || '',
      description: value.description || '',
    };
  }

  static formatExtraFormValue(form?: FormGroup<IListingExtraForm>): IListingExtra {
    if (!form) {
      return {};
    }

    const value = form.value;

    return {
      id: value.id || undefined,
      label: value.label || undefined,
      name: value.name || undefined,
      price: value.price ?? undefined,
      description: value.description || undefined,
      count: value.count ?? undefined,
    };
  }

  static formatActivityFormValue(form?: FormGroup<IActivityForm>): IActivity {
    if (!form) {
      return {};
    }

    const value = form.value;

    return {
      id: value.id || undefined,
      type: value.type || undefined,
      typeCustom: value.typeCustom || undefined,
      name: value.name || undefined,
      description: value.description || undefined,
      photos: formatPhotosFormValue(form.get('photos') as FormArray<FormGroup<IPhotoForm>>),
      url: value.url || undefined,
    };
  }

  static formatLandOwnerFormValue(form?: FormGroup<ILandOwnerForm>): ILandOwner {
    if (!form) {
      return {};
    }

    const value = form.value;

    return {
      id: value.id || undefined,
      self: value.self === null ? undefined : value.self,
      private: value.private === null ? undefined : value.private,
      firstName: value.firstName || undefined,
      lastName: value.lastName || undefined,
      phone: value.phone || undefined,
      address: formatAddressFormValue(form.get('address') as FormGroup<IAddressForm>),
      represent: value.represent === null ? undefined : value.represent,
    };
  }

  constructor(
    private formBuilder: FormBuilder,
    private listingFacade?: ListingFacade,
    private alertFacade?: AlertFacade,
  ) {
    this.accessRouteGroup = (): IAccessRouteForm => {
      return {
        id: formBuilder.control<number | null>(null),
        name: formBuilder.control<string | null>(''),
        description: formBuilder.control<string | null>('', [Validators.minLength(8)]),
        directions: formBuilder.control<string | null>(''),
        startingPoint: coordinatesFormBuilder(formBuilder),
        maxHeight: formBuilder.control<number | null>(null, [Validators.min(0)]),
        maxLength: formBuilder.control<number | null>(null, [Validators.min(0)]),
        maxWidth: formBuilder.control<number | null>(null, [Validators.min(0)]),
        minClearance: formBuilder.control<number | null>(null, [Validators.min(0)]),
        intermediateWaypoints: formBuilder.array<FormGroup<IWaypointForm>>([], { updateOn: 'blur' }),
        roadCondition: roadConditionsFormBuilder(formBuilder),
      };
    };

    this.activityGroup = (activityType?: IKvPair): IActivityForm => {
      return {
        id: formBuilder.control<number | null>(null),
        type: formBuilder.control<IActivityType['type'] | null>((activityType?.key as IActivityType['type']) || null),
        typeCustom: formBuilder.control(''),
        name: formBuilder.control(activityType ? activityType.value : '', [Validators.maxLength(120)]),
        description: formBuilder.control(''),
        // todo: why is address and coordinates here
        address: addressFormBuilder(formBuilder),
        coordinates: coordinatesFormBuilder(formBuilder),
        photos: formBuilder.array<FormGroup<IPhotoForm>>([], { updateOn: 'blur' }),
        url: formBuilder.control(''),
      };
    };

    this.landOwnerGroup = (): ILandOwnerForm => {
      return {
        id: formBuilder.control<number | null>(null),
        self: formBuilder.control<boolean | null>(null),
        private: formBuilder.control<boolean | null>(null),
        firstName: formBuilder.control('', [Validators.maxLength(120)]),
        lastName: formBuilder.control('', [Validators.maxLength(120)]),
        phone: formBuilder.control('', [Validators.maxLength(20)]),
        address: addressFormBuilder(formBuilder),
        represent: formBuilder.control<boolean | null>(null),
      };
    };

    this.noiseGroup = (): INoiseForm => {
      return {
        level: formBuilder.control<'silence' | 'nature' | 'periodic' | 'constant' | null>(null),
        description: formBuilder.control<string | null>(''),
      };
    };

    this.listingForm = formBuilder.group<IListingForm>(
      {
        id: formBuilder.control<number | null>(null),
        syncId: formBuilder.control<string | null>(null),
        available: formBuilder.control<boolean | null>(null),
        name: formBuilder.control<string | null>('', [Validators.minLength(8), Validators.maxLength(64)]),
        landTypes: formBuilder.array<ILandType['type']>([], { updateOn: 'change' }),
        coordinates: coordinatesFormBuilder(formBuilder),
        reservationProcess: formBuilder.control<IReservationProcessType['type'] | null>('standard', {
          updateOn: 'change',
        }),
        checkInProcess: formBuilder.control<ICheckInProcessType['type'] | null>('meetAndGreet', { updateOn: 'change' }),
        checkIn: formBuilder.control<string | null>(null, {
          validators: [Validators.minLength(8), Validators.maxLength(8)],
          updateOn: 'change',
        }), // time '' '04:00PM'
        checkOut: formBuilder.control<string | null>(null, {
          validators: [Validators.minLength(8), Validators.maxLength(8)],
          updateOn: 'change',
        }), // time '' '11:00AM'
        description: formBuilder.control<string | null>('', [Validators.minLength(150), Validators.maxLength(3000)]),
        price: formBuilder.control<number | null>(null, { updateOn: 'change', validators: [Validators.min(0)] }),
        weekdayDiscount: formBuilder.control<number | null>(0, [Validators.min(0)]),
        nextReservationDiscount: formBuilder.control<number | null>(null, {
          validators: [Validators.min(0)],
          updateOn: 'change',
        }),
        // array of months for which the site is unavailable (1 = January, 12 = December)
        blackoutMonths: formBuilder.array<number>([], { updateOn: 'change' }),
        blackoutDays: formBuilder.array<string>([], { updateOn: 'blur' }),
        photos: formBuilder.array<FormGroup<IPhotoForm>>([], { updateOn: 'blur' }), // array of URL ''s
        maxGuests: formBuilder.control<number | null>(null, [Validators.min(0)]),
        size: formBuilder.control<number | null>(null, [Validators.min(0)]),
        extras: formBuilder.array<FormGroup<IListingExtraForm>>([], { updateOn: 'blur' }),
        signalReception: formBuilder.array<FormGroup<ISignalReceptionForm>>([], { updateOn: 'change' }),
        maxRVAge: formBuilder.control<number | null>(null, [Validators.min(0), Validators.max(150)]),
        minLengthOfStayInDays: formBuilder.control<number | null>(null, {
          validators: [Validators.min(1), Validators.max(365)],
          updateOn: 'change',
        }),
        maxLengthOfStayInDays: formBuilder.control<number | null>(null, {
          validators: [Validators.min(1)],
          updateOn: 'change',
        }),
        bookingWindow: formBuilder.control<number | null>(365, { updateOn: 'change' }),
        reserveDaysInAdvance: formBuilder.control<number | null>(null, {
          validators: [Validators.min(0), Validators.max(365)],
          updateOn: 'change',
        }),
        cancellationPolicy: formBuilder.control<ICancellationPolicyOption['option'] | null>(null, {
          updateOn: 'change',
        }),
        noise: formBuilder.group(this.noiseGroup(), { updateOn: 'change' }),
        policies: formBuilder.array<FormGroup<IPolicyForm>>([], { updateOn: 'change' }),
        landOwner: formBuilder.group(this.landOwnerGroup(), {
          updateOn: 'blur',
        }),
        address: addressFormBuilder(formBuilder),
        phone: formBuilder.control<string | null>('', [Validators.maxLength(20)]),
        email: formBuilder.control<string | null>(''),
        url: formBuilder.control<string | null>(''),
        potentialHazards: formBuilder.control<boolean | null>(false, { updateOn: 'change' }),
        potentialHazardDescription: formBuilder.control<string | null>(''),
      },
      { updateOn: 'blur' },
    );
  }

  patchSiteValue(form: FormGroup<IListingForm>, listing: IListing): FormGroup<IListingForm> {
    form.patchValue({
      ...listing,
      id: listing.id ? listing.id : undefined,
      available: listing.available === null ? undefined : listing.available,
      name: listing.name ? listing.name : undefined,
      landTypes: listing.landTypes ? listing.landTypes : undefined,
      coordinates: listing.coordinates ? listing.coordinates : undefined,
      reservationProcess: listing.reservationProcess ? listing.reservationProcess : undefined,
      checkInProcess: listing.checkInProcess ? listing.checkInProcess : undefined,
      checkIn: listing.checkIn ? listing.checkIn : undefined,
      checkOut: listing.checkOut ? listing.checkOut : undefined,
      description: listing.description ? listing.description : undefined,
      weekdayDiscount: listing.weekdayDiscount ? listing.weekdayDiscount : undefined,
      nextReservationDiscount: listing.nextReservationDiscount === null ? undefined : listing.nextReservationDiscount,
      blackoutMonths: listing.blackoutMonths ? listing.blackoutMonths : undefined,
      blackoutDays: listing.blackoutDays ? listing.blackoutDays : undefined,
      photos: listing.photos ? listing.photos : undefined,
      size: listing.size ? listing.size : undefined,
      extras: listing.extras ? listing.extras : undefined,
      signalReception: listing.signalReception ? listing.signalReception : undefined,
      maxRVAge: listing.maxRVAge ? listing.maxRVAge : undefined,
      minLengthOfStayInDays: listing.minLengthOfStayInDays ? listing.minLengthOfStayInDays : undefined,
      maxLengthOfStayInDays: listing.maxLengthOfStayInDays ? listing.maxLengthOfStayInDays : undefined,
      bookingWindow: listing.bookingWindow ? listing.bookingWindow : undefined,
      reserveDaysInAdvance: listing.reserveDaysInAdvance ? listing.reserveDaysInAdvance : undefined,
      cancellationPolicy: listing.cancellationPolicy ? listing.cancellationPolicy : undefined,
      policies: listing.policies ? listing.policies : undefined,
      landOwner: listing.landOwner ? listing.landOwner : undefined,
      address: listing.address ? listing.address : undefined,
      potentialHazards:
        listing.potentialHazards === null || listing.potentialHazards === undefined
          ? undefined
          : listing.potentialHazards,
      potentialHazardDescription: listing.potentialHazardDescription ? listing.potentialHazardDescription : undefined,
    });

    return form;
  }

  formatSiteFormValue(form: FormGroup<IListingForm>): ISite {
    return ListingForm.formatListingFormValue(form);
  }

  formatNoiseFormValue(form?: FormGroup<INoiseForm>): INoise {
    return ListingForm.formatNoiseFormValue(form);
  }

  formatAccessRouteFormValue(form?: FormGroup<IAccessRouteForm>): IAccessRoute {
    return ListingForm.formatAccessRouteFormValue(form);
  }

  formatActivityFormValue(form?: FormGroup<IActivityForm>): IActivity {
    return ListingForm.formatActivityFormValue(form);
  }

  formatLandOwnerFormValue(form?: FormGroup<ILandOwnerForm>): ILandOwner {
    return ListingForm.formatLandOwnerFormValue(form);
  }

  update(listing: Listing) {
    if (!listing) {
      return;
    }

    this.patchSiteValue(this.listingForm, listing);

    this.syncArrayField(listing.photos, this.photos, photoFormBuilder);
    this.syncArrayField(listing.blackoutMonths, this.blackoutMonths);
    this.syncArrayField(listing.blackoutDays, this.blackoutDays);
    this.syncArrayField(listing.extras, this.extras, extraFormBuilder);

    if (listing.policies) {
      // Make sure the form policies array is at least as short as the incoming array with one extra space
      // while (this.site.policies.length < this.policies.length) {
      //   this.policies.removeAt(this.policies.length - 1);
      // }

      // Add or replace each element in the form array with the incoming element
      listing.policies.forEach((policy, index) => {
        if (this.policies) {
          if (this.policies.length > index) {
            this.policies.controls[index].patchValue(policy);
          } else {
            this.policies.push(policyFormBuilder(this.formBuilder, policy));
          }
        }
      });
    }

    this.syncArrayField(listing.signalReception, this.signalReception, signalReceptionFormBuilder);
    this.syncArrayField(listing.landTypes, this.landTypes);

    this.listingForm.markAsPristine();
  }

  /**
   * Returns boolean to indicate whether or not saving was necessary.
   * @param {IListing} listingToSave
   * @param {() => void} beforeSaveCallback
   * @return boolean
   */
  save(listingToSave: IListing, beforeSaveCallback?: () => void): boolean {
    this.errors.length = 0;
    const errorHash: { [id: string]: string } = {};

    getFormValidationErrors(this.listingForm).forEach((error) => (errorHash[error] = ''));

    this.errors = Object.keys(errorHash);

    if (this.errors.length === 0 && this.listingForm.valid && this.listingForm.dirty) {
      if (beforeSaveCallback) {
        beforeSaveCallback();
      }
      this.listingFacade?.update(listingToSave);
      const checkInInput = this.listingForm.controls.checkIn;
      const checkOutInput = this.listingForm.controls.checkOut;

      if (checkInInput) {
        checkInInput.markAsPristine();
        checkInInput.markAsUntouched();
      }

      if (checkOutInput) {
        checkOutInput.markAsPristine();
        checkOutInput.markAsUntouched();
      }

      this.listingForm.markAsPristine();
      this.lastSaved = DateTime.local();
      return true;
    } else if (this.errors.length > 0 && !this.listingForm.valid && this.listingForm.dirty) {
      const errorList = this.errors.join('\n');
      this.alertFacade?.display({
        level: 'error',
        body: `The form has unresolved errors:\n${errorList}`,
        title: 'Changes Have Not Been Saved',
      });
      return false;
    } else {
      this.lastSaved = DateTime.local();
      return false;
    }
  }

  private syncArrayField(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    source?: any[],
    destination?: UntypedFormArray,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formBuilder?: (formBuilder: FormBuilder, argv?: any) => UntypedFormGroup,
  ) {
    if (source && destination) {
      // Make sure the form array is at least as short as the incoming array
      while (source.length < destination.length) {
        destination.removeAt(destination.length - 1);
      }
      // Add or replace each element in the from array with the incoming element
      source.forEach((sourceEl, index) => {
        if (destination) {
          if (destination.length > index) {
            destination.controls[index].setValue(sourceEl);
          } else {
            if (formBuilder) {
              destination.push(formBuilder(this.formBuilder, sourceEl));
            } else {
              destination.push(this.formBuilder.control(sourceEl));
            }
          }
        }
      });
    }
  }
}
